import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getPatient,
  getPatientConsultations,
  getPatientLatestScales,
  checkProfileComplete,
  getPatientBEERSStatus,
  getPatientDraft,
  updatePatient,
} from "../lib/api";
import { detectGeriatricSyndromes } from "../lib/clinical-recommendations";
import { LAWTON_ITEMS, evaluateCam, evaluateMiniCog, interpretPolifarmaciaSimple, scoreYesavageFromAnswers, interpretYesavageLabel } from "../lib/scales-data";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  User, Calendar, Pill, AlertTriangle, Activity,
  FileText, ClipboardPlus, ArrowLeft, Heart, Brain, Smile, Loader2,
  TrendingUp, Apple, Users,
  Stethoscope, Clock, ChevronRight, AlertCircle, CheckCircle2,
  Scale, Eye, Zap, BarChart2, BookOpen, Save, X, Plus, Pencil, FileStack,
} from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import ExternalAssessmentsSection from "../components/ExternalAssessmentsSection";

// ============ HELPERS ============
// scaleHistory se construye desde consultations[].escalas (Bloque 5).
const ENABLE_SCALES_HISTORY = true;

/** Extrae puntaje de consultation.escalas (compatible con puntaje/value/score y mna/mna_sf). */
function extractScaleValue(escalas, scaleId) {
  if (!escalas || typeof escalas !== "object") return null;
  const ids = ["mna_sf", "mna"].includes(scaleId) ? ["mna_sf", "mna"] : [scaleId];
  for (const sid of ids) {
    const v = escalas[sid];
    if (v == null) continue;
    let raw = typeof v === "object" ? (v.puntaje ?? v.value ?? v.score) : v;
    if (raw == null || String(raw).trim() === "") continue;
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

const SCALE_LABELS = {
  barthel: "Barthel",
  lawton: "Lawton",
  katz: "Katz",
  mmse: "MMSE",
  pfeiffer: "Pfeiffer",
  yesavage: "Yesavage GDS",
  mna_sf: "MNA-SF",
  morse: "Morse",
  tinetti: "Tinetti",
  frail: "FRAIL",
  cfs: "CFS",
  charlson: "Charlson",
  gijon: "Gijón",
  eva_dolor: "EVA Dolor",
  norton: "Norton",
  gad7: "GAD-7",
  sarc_f: "SARC-F",
  tug: "TUG",
  cam: "CAM",
  mini_cog: "Mini-Cog",
  moca: "MoCA",
  polifarmacia_simple: "Polifarmacia simple",
  phq9: "PHQ-9",
  sppb: "SPPB",
  zarit: "Zarit",
};

/** Texto clínico legible: sin None, espacios duplicados ni ruido típico de placeholders. */
function normalizeClinicalSnippet(raw) {
  if (raw == null) return "";
  let s = String(raw).replace(/\u00a0/g, " ");
  s = s.replace(/\bnone\b/gi, " ").replace(/\s+/g, " ").trim();
  return s;
}

const _GARBAGE_HISTORY_SUBSTRINGS = [
  "sin registrar",
  "pendiente de",
  "por definir",
  "no aplica",
  "n/a",
  "en evaluación",
  "a definir",
];

function isUsefulClinicalHistoryText(text) {
  const t = normalizeClinicalSnippet(text);
  if (t.length < 4) return false;
  const low = t.toLowerCase();
  if (_GARBAGE_HISTORY_SUBSTRINGS.some((g) => low.includes(g))) return false;
  if (/^[—\-_.\s]+$/.test(t)) return false;
  return true;
}

/**
 * Por consulta: prioriza historia_enfermedad_actual → motivo_consulta → impresion_clinica.
 */
function extractConsultHistoryFragment(consultation) {
  if (!consultation) return "";
  const h = normalizeClinicalSnippet(consultation.historia_enfermedad_actual);
  const m = normalizeClinicalSnippet(consultation.motivo_consulta);
  const i = normalizeClinicalSnippet(consultation.impresion_clinica);
  if (isUsefulClinicalHistoryText(h)) return h;
  if (isUsefulClinicalHistoryText(m)) return m;
  if (isUsefulClinicalHistoryText(i)) return i;
  return "";
}

/**
 * Fallback cuando no hay historia persistida en perfil: resume hasta 3 fragmentos
 * (consultas más recientes primero; fecha_consulta || created_at).
 */
function buildLongitudinalHistoryFromConsultations(consultations) {
  if (!Array.isArray(consultations) || consultations.length === 0) return "";

  const sorted = [...consultations].sort((a, b) => {
    const ta = Date.parse(a.fecha_consulta || a.created_at || "") || 0;
    const tb = Date.parse(b.fecha_consulta || b.created_at || "") || 0;
    return tb - ta;
  });

  const fragments = [];
  const seenNorm = new Set();

  for (const c of sorted) {
    if (fragments.length >= 3) break;
    const frag = extractConsultHistoryFragment(c);
    if (!frag) continue;
    const norm = frag.toLowerCase().replace(/\s+/g, " ").trim();
    if (seenNorm.has(norm)) continue;
    seenNorm.add(norm);
    let piece = frag.trim();
    if (piece && !/[.!?…]$/.test(piece)) piece += ".";
    fragments.push(piece);
  }

  return fragments.join(" ").replace(/\s+/g, " ").trim();
}

/** Historia persistida en paciente: latest_historia (editor) o historia_enfermedad_actual legada. */
function getPersistedPatientHistoria(patient) {
  if (!patient) return "";
  if (isUsefulClinicalHistoryText(patient.latest_historia)) return String(patient.latest_historia).trim();
  if (isUsefulClinicalHistoryText(patient.historia_enfermedad_actual)) {
    return String(patient.historia_enfermedad_actual).trim();
  }
  return "";
}
const ENABLE_BEERS_MODULE = false; // /api/beers/patient/{id}
const RiskBadge = ({ level }) => {
  const styles = {
    alto: "bg-red-100 text-red-700 border-red-200",
    medio: "bg-amber-100 text-amber-700 border-amber-200",
    bajo: "bg-emerald-100 text-emerald-700 border-emerald-200"
  };
  const labels = { alto: "Alto Riesgo", medio: "Riesgo Medio", bajo: "Bajo Riesgo" };
  return (
    <Badge variant="outline" className={`${styles[level] || styles.bajo} border font-medium`}>
      {labels[level] || "Bajo Riesgo"}
    </Badge>
  );
};

// ============ CLINICAL STATUS CARD (top-level at-a-glance summary) ============
const ClinicalStatusPanel = ({ displayScales, detectedSyndromes, latestConsultation }) => {
  const domains = [
    {
      key: "funcional",
      label: "Funcional",
      icon: Activity,
      getValue: () => {
        const b = displayScales?.barthel?.puntaje ?? displayScales?.barthel?.value;
        const k = displayScales?.katz?.puntaje ?? displayScales?.katz?.value;
        if (b !== undefined) return { score: b, max: 100, label: b >= 90 ? "Independiente" : b >= 60 ? "Dep. leve" : b >= 40 ? "Dep. moderada" : "Dep. severa", risk: b >= 90 ? "bajo" : b >= 60 ? "medio" : "alto", sub: `Barthel ${b}/100` };
        if (k !== undefined) return { score: k, max: 6, label: k === 6 ? "Independiente" : k >= 4 ? "Dep. leve" : "Dep. severa", risk: k === 6 ? "bajo" : k >= 4 ? "medio" : "alto", sub: `Katz ${k}/6` };
        return null;
      }
    },
    {
      key: "cognitivo",
      label: "Cognitivo",
      icon: Brain,
      getValue: () => {
        const m = displayScales?.mmse?.puntaje ?? displayScales?.mmse?.value;
        const p = displayScales?.pfeiffer?.puntaje ?? displayScales?.pfeiffer?.value;
        if (m !== undefined) return { score: m, max: 30, label: m >= 24 ? "Normal" : m >= 18 ? "Deterioro leve" : m >= 10 ? "Moderado" : "Severo", risk: m >= 24 ? "bajo" : m >= 18 ? "medio" : "alto", sub: `MMSE ${m}/30` };
        if (p !== undefined) return { score: 10 - p, max: 10, label: p <= 2 ? "Normal" : p <= 4 ? "Deterioro leve" : p <= 7 ? "Moderado" : "Severo", risk: p <= 2 ? "bajo" : p <= 4 ? "medio" : "alto", sub: `Pfeiffer ${p} errores` };
        return null;
      }
    },
    {
      key: "fragilidad",
      label: "Fragilidad",
      icon: Zap,
      getValue: () => {
        const f = displayScales?.frail?.puntaje ?? displayScales?.frail?.value;
        const c = displayScales?.cfs?.puntaje ?? displayScales?.cfs?.value;
        if (f !== undefined) return { score: 5 - f, max: 5, label: f === 0 ? "Robusto" : f <= 2 ? "Pre-frágil" : "Frágil", risk: f === 0 ? "bajo" : f <= 2 ? "medio" : "alto", sub: `FRAIL ${f}/5` };
        if (c !== undefined) return { score: 9 - c, max: 9, label: c <= 3 ? "Buena forma" : c <= 5 ? "Vulnerable" : "Frágil", risk: c <= 3 ? "bajo" : c <= 5 ? "medio" : "alto", sub: `CFS ${c}/9` };
        return null;
      }
    },
    {
      key: "caidas",
      label: "Riesgo Caídas",
      icon: AlertTriangle,
      getValue: () => {
        const mo = displayScales?.morse?.puntaje ?? displayScales?.morse?.value;
        const ti = displayScales?.tinetti?.puntaje ?? displayScales?.tinetti?.value;
        if (mo !== undefined) return { score: 125 - mo, max: 125, label: mo <= 24 ? "Bajo" : mo <= 44 ? "Moderado" : "Alto", risk: mo <= 24 ? "bajo" : mo <= 44 ? "medio" : "alto", sub: `Morse ${mo}` };
        if (ti !== undefined) return { score: ti, max: 28, label: ti >= 24 ? "Bajo" : ti >= 19 ? "Moderado" : "Alto", risk: ti >= 24 ? "bajo" : ti >= 19 ? "medio" : "alto", sub: `Tinetti ${ti}/28` };
        return null;
      }
    },
    {
      key: "nutricional",
      label: "Nutrición",
      icon: Apple,
      getValue: () => {
        const n = displayScales?.mna_sf?.puntaje ?? displayScales?.mna_sf?.value;
        if (n !== undefined) return { score: n, max: 14, label: n >= 12 ? "Normal" : n >= 8 ? "Riesgo" : "Malnutrición", risk: n >= 12 ? "bajo" : n >= 8 ? "medio" : "alto", sub: `MNA ${n}/14` };
        return null;
      }
    },
    {
      key: "emocional",
      label: "Ánimo",
      icon: Smile,
      getValue: () => {
        const y = displayScales?.yesavage?.puntaje ?? displayScales?.yesavage?.value;
        if (y !== undefined) return { score: 15 - y, max: 15, label: y <= 4 ? "Normal" : y <= 9 ? "Depresión leve" : "Depresión severa", risk: y <= 4 ? "bajo" : y <= 9 ? "medio" : "alto", sub: `Yesavage ${y}/15` };
        return null;
      }
    }
  ];

  const riskColors = {
    bajo: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-500", dot: "bg-emerald-500" },
    medio: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", bar: "bg-amber-500", dot: "bg-amber-500" },
    alto: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", bar: "bg-red-500", dot: "bg-red-500" },
    sin_datos: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-400", bar: "bg-slate-200", dot: "bg-slate-300" }
  };

  return (
    <Card className="border-slate-200 bg-white">
      <CardHeader className="pb-3 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-sky-600" />
          Estado Clínico Actual
          {detectedSyndromes.length > 0 && (
            <Badge className="ml-auto bg-red-100 text-red-700 border border-red-200 text-xs font-medium">
              {detectedSyndromes.length} síndrome{detectedSyndromes.length > 1 ? "s" : ""} detectado{detectedSyndromes.length > 1 ? "s" : ""}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {domains.map(domain => {
            const val = domain.getValue();
            const risk = val ? val.risk : "sin_datos";
            const c = riskColors[risk];
            const Icon = domain.icon;
            const pct = val ? Math.round((val.score / val.max) * 100) : 0;
            return (
              <div key={domain.key} className={`${c.bg} ${c.border} border rounded-xl p-3 flex flex-col gap-1.5`}>
                <div className="flex items-center gap-1.5">
                  <Icon className={`h-3.5 w-3.5 ${c.text}`} />
                  <span className="text-xs font-medium text-slate-600">{domain.label}</span>
                </div>
                {val ? (
                  <>
                    <p className={`text-sm font-bold ${c.text}`}>{val.label}</p>
                    <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                      <div className={`h-full ${c.bar} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-500">{val.sub}</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">Sin datos</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Detected syndromes strip */}
        {detectedSyndromes.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">Síndromes detectados</p>
            <div className="flex flex-wrap gap-1.5">
              {detectedSyndromes.map((s, i) => {
                const isHighRisk = ["fragilidad", "riesgo_caidas", "deterioro_cognitivo", "dependencia_funcional"].includes(s.id);
                return (
                  <span key={i} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${isHighRisk ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${isHighRisk ? "bg-red-500" : "bg-amber-500"}`} />
                    {s.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ GERIATRIC PROFILE VISUAL (legacy colored tiles) ============
const GeriatricProfileVisual = ({ scales }) => {
  const categories = [
    { 
      id: "funcional", 
      label: "Funcional", 
      icon: Activity,
      getStatus: () => {
        const barthel = scales?.barthel?.puntaje;
        if (barthel === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        if (barthel >= 90) return { status: "stable", label: "Estable", scales: `Barthel: ${barthel}` };
        if (barthel >= 60) return { status: "risk", label: "Riesgo", scales: `Barthel: ${barthel}` };
        return { status: "high-risk", label: "Alto Riesgo", scales: `Barthel: ${barthel}` };
      }
    },
    { 
      id: "cognitivo", 
      label: "Cognitivo", 
      icon: Brain,
      getStatus: () => {
        const mmse = scales?.mmse?.puntaje;
        const pfeiffer = scales?.pfeiffer?.puntaje;
        if (mmse !== undefined) {
          if (mmse >= 24) return { status: "stable", label: "Estable", scales: `MMSE: ${mmse}` };
          if (mmse >= 18) return { status: "risk", label: "Riesgo", scales: `MMSE: ${mmse}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `MMSE: ${mmse}` };
        }
        if (pfeiffer !== undefined) {
          if (pfeiffer <= 2) return { status: "stable", label: "Estable", scales: `Pfeiffer: ${pfeiffer}` };
          if (pfeiffer <= 4) return { status: "risk", label: "Riesgo", scales: `Pfeiffer: ${pfeiffer}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Pfeiffer: ${pfeiffer}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      }
    },
    { 
      id: "nutricional", 
      label: "Nutricional", 
      icon: Apple,
      getStatus: () => {
        const mna = scales?.mna_sf?.puntaje;
        if (mna === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        if (mna >= 12) return { status: "stable", label: "Estable", scales: `MNA: ${mna}` };
        if (mna >= 8) return { status: "risk", label: "Riesgo", scales: `MNA: ${mna}` };
        return { status: "high-risk", label: "Alto Riesgo", scales: `MNA: ${mna}` };
      }
    },
    { 
      id: "caidas", 
      label: "Caídas", 
      icon: AlertTriangle,
      getStatus: () => {
        const morse = scales?.morse?.puntaje;
        const tinetti = scales?.tinetti?.puntaje;
        if (morse !== undefined) {
          if (morse <= 24) return { status: "stable", label: "Bajo Riesgo", scales: `Morse: ${morse}` };
          if (morse <= 44) return { status: "risk", label: "Riesgo Medio", scales: `Morse: ${morse}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Morse: ${morse}` };
        }
        if (tinetti !== undefined) {
          if (tinetti >= 24) return { status: "stable", label: "Bajo Riesgo", scales: `Tinetti: ${tinetti}` };
          if (tinetti >= 19) return { status: "risk", label: "Riesgo Medio", scales: `Tinetti: ${tinetti}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Tinetti: ${tinetti}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      }
    },
    { 
      id: "fragilidad", 
      label: "Fragilidad", 
      icon: Heart,
      getStatus: () => {
        const frail = scales?.frail?.puntaje;
        const cfs = scales?.cfs?.puntaje;
        if (frail !== undefined) {
          if (frail === 0) return { status: "stable", label: "Robusto", scales: `FRAIL: ${frail}` };
          if (frail <= 2) return { status: "risk", label: "Pre-frágil", scales: `FRAIL: ${frail}` };
          return { status: "high-risk", label: "Frágil", scales: `FRAIL: ${frail}` };
        }
        if (cfs !== undefined) {
          if (cfs <= 3) return { status: "stable", label: "Estable", scales: `CFS: ${cfs}` };
          if (cfs <= 5) return { status: "risk", label: "Riesgo", scales: `CFS: ${cfs}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `CFS: ${cfs}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      }
    },
    { 
      id: "emocional", 
      label: "Emocional", 
      icon: Smile,
      getStatus: () => {
        const yesavage = scales?.yesavage?.puntaje;
        if (yesavage === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        if (yesavage <= 4) return { status: "stable", label: "Normal", scales: `Yesavage: ${yesavage}` };
        if (yesavage <= 8) return { status: "risk", label: "Depresión leve", scales: `Yesavage: ${yesavage}` };
        return { status: "high-risk", label: "Depresión", scales: `Yesavage: ${yesavage}` };
      }
    }
  ];

  const statusStyles = {
    stable: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
    risk: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
    "high-risk": { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
    unknown: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", dot: "bg-slate-300" }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="h-4 w-4 text-sky-600" />
          Perfil Geriátrico Visual
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {categories.map(cat => {
            const result = cat.getStatus();
            const style = statusStyles[result.status];
            const Icon = cat.icon;
            
            return (
              <div 
                key={cat.id}
                className={`${style.bg} ${style.border} border rounded-lg p-3 transition-all hover:shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-4 w-4 ${style.text}`} />
                  <span className="text-xs font-medium text-slate-700">{cat.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`h-2 w-2 rounded-full ${style.dot}`} />
                  <span className={`text-sm font-semibold ${style.text}`}>{result.label}</span>
                </div>
                {result.scales && (
                  <p className="text-xs text-slate-500 mt-1">{result.scales}</p>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t">
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            Estable
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="h-2 w-2 rounded-full bg-amber-500" />
            Riesgo
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            Alto Riesgo
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="h-2 w-2 rounded-full bg-slate-300" />
            Sin datos
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Evolution Chart Component
const EvolutionChart = ({ data, title, dataKey, color, maxValue }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
        Sin datos de evolución
      </div>
    );
  }

  return (
    <div className="h-32">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="fecha" 
            tick={{ fontSize: 10, fill: '#94a3b8' }} 
            axisLine={{ stroke: '#e2e8f0' }}
          />
          <YAxis 
            domain={[0, maxValue || 'auto']} 
            tick={{ fontSize: 10, fill: '#94a3b8' }} 
            axisLine={{ stroke: '#e2e8f0' }}
          />
          <Tooltip 
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value) => [value, title]}
          />
          <Area 
            type="monotone" 
            dataKey={dataKey} 
            stroke={color} 
            strokeWidth={2}
            fill={`url(#gradient-${dataKey})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main Patient Dashboard Component
export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemoUser = user?.email === "demo@aurorageriatrica.com";
  
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [latestScales, setLatestScales] = useState({});
  const [scaleHistory, setScaleHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);
  const [beersStatus, setBeersStatus] = useState(null);

  // Tab navigation: "perfil" | "historia"
  const [activeTab, setActiveTab] = useState("perfil");

  // Longitudinal history edit state (mirrors db.patients fields)
  const [histEdit, setHistEdit] = useState(null); // null = read mode, object = edit mode
  const [savingHist, setSavingHist] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [patientRes, consultRes, scalesRes, profileRes, draftRes] = await Promise.all([
          getPatient(id),
          getPatientConsultations(id),
          getPatientLatestScales(id).catch(() => ({ data: {} })),
          checkProfileComplete().catch(() => ({ data: { complete: false } })),
          getPatientDraft(id).catch(() => ({ data: null }))
        ]);

        setPatient(patientRes.data);
        const draft = draftRes?.data;
        setHasDraft(!!(draft && draft.id));
        const consultList = consultRes.data || [];
        setConsultations(consultList);
        setLatestScales(scalesRes?.data || {});
        setProfileComplete(profileRes?.data?.complete ?? false);

        // Build scaleHistory from consultations[].escalas (Bloque 5).
        if (ENABLE_SCALES_HISTORY && consultList.length > 0) {
          const scaleIds = ["barthel", "mmse", "morse", "katz", "pfeiffer", "yesavage", "mna_sf"];
          const sorted = [...consultList].sort((a, b) => {
            const da = new Date(a.fecha_consulta || a.created_at || 0).getTime();
            const db = new Date(b.fecha_consulta || b.created_at || 0).getTime();
            return da - db;
          });
          const history = {};
          scaleIds.forEach(scaleId => {
            const points = [];
            sorted.forEach(c => {
              const val = extractScaleValue(c.escalas, scaleId);
              if (val != null) {
                const raw = c.fecha_consulta || c.created_at;
                points.push({
                  fecha: raw ? new Date(raw).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "",
                  value: val
                });
              }
            });
            if (points.length > 0) history[scaleId] = points;
          });
          setScaleHistory(history);
        } else {
          setScaleHistory({});
        }

        // Fetch BEERS status
        if (ENABLE_BEERS_MODULE) {
          try {
            const beersRes = await getPatientBEERSStatus(id);
            setBeersStatus(beersRes.data);
          } catch {
            setBeersStatus(null);
          }
        } else {
          setBeersStatus(null);
        }

      } catch (error) {
        toast.error("Error al cargar datos del paciente");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Get latest consultation
  const latestConsultation = consultations[0];

  const latestConsultationScales = (() => {
    const byVisit = {};
    if (!latestConsultation) return byVisit;

    const cEscalas = latestConsultation.escalas || {};
    Object.entries(cEscalas).forEach(([k, v]) => {
      if (!v) return;
      const puntaje = typeof v === "object" ? (v.value ?? v.puntaje) : v;
      if (puntaje !== undefined && puntaje !== null) {
        byVisit[k] = {
          puntaje: typeof puntaje === "object" ? puntaje?.value : puntaje,
          interpretacion: v?.interpretacion || null,
          nivel_riesgo: v?.nivel_riesgo || null,
        };
      }
    });

    const scaleAnswers = latestConsultation.scale_answers || {};
    Object.entries(scaleAnswers).forEach(([scaleId, answers]) => {
      if (!answers || Object.keys(answers).length === 0) return;
      if (byVisit[scaleId]?.puntaje !== undefined) return;
      let score = 0;
      if (scaleId === "yesavage") {
        score = scoreYesavageFromAnswers(answers);
      } else if (scaleId === "cam") {
        const KEYS = ["inicio_agudo", "inatencion", "pensamiento_desorganizado", "alteracion_conciencia"];
        if (!KEYS.every((k) => answers[k] === "si" || answers[k] === "no")) return;
        score = evaluateCam(answers);
      } else if (scaleId === "polifarmacia_simple") {
        const raw = answers.num_medicamentos;
        if (raw === undefined || raw === null || String(raw).trim() === "") return;
        const n = Number(raw);
        if (Number.isNaN(n)) return;
        const flags = {
          duplicados: Boolean(answers.duplicados),
          potencialmente_inapropiados: Boolean(answers.potencialmente_inapropiados),
          dificultad_adherencia: Boolean(answers.dificultad_adherencia),
        };
        byVisit[scaleId] = {
          puntaje: n,
          interpretacion: interpretPolifarmaciaSimple(n, flags),
          nivel_riesgo: null,
        };
        return;
      } else if (scaleId === "mini_cog") {
        const mc = evaluateMiniCog(answers);
        if (mc === null) return;
        score = mc;
      } else if (scaleId !== "lawton") {
        score = Object.values(answers).reduce((s, v) => s + (parseInt(v) || 0), 0);
      } else {
        score = (LAWTON_ITEMS || []).reduce((s, item) => {
          const raw = answers[item.id];
          if (raw === undefined) return s;
          const opt = item.options?.find((o) => o.optId === raw || o.optId === parseInt(raw));
          return s + (opt?.value || 0);
        }, 0);
      }
      byVisit[scaleId] = { puntaje: score, interpretacion: null, nivel_riesgo: null };
    });
    return byVisit;
  })();

  // Source of truth: latest consultation; fallback only when the visit has no scales.
  const displayScales = Object.keys(latestConsultationScales).length > 0 ? latestConsultationScales : { ...latestScales };

  // Build scale scores for syndrome detection
  const scaleScoresForSyndromes = Object.fromEntries(
    Object.entries(displayScales).map(([k,v]) => [k, v?.puntaje ?? v?.value])
  );
  const detectedSyndromes = detectGeriatricSyndromes(scaleScoresForSyndromes);

  // Calculate follow-up recommendations (use displayScales for merged data)
  const getFollowUpRecommendations = () => {
    const recs = [];
    const barthel = displayScales?.barthel?.puntaje ?? displayScales?.barthel?.value;
    const mmse = displayScales?.mmse?.puntaje ?? displayScales?.mmse?.value;
    const yesavage = displayScales?.yesavage?.puntaje ?? displayScales?.yesavage?.value;
    const morse = displayScales?.morse?.puntaje ?? displayScales?.morse?.value;
    const mna = displayScales?.mna_sf?.puntaje ?? displayScales?.mna_sf?.value;

    if (barthel !== undefined && barthel < 60) {
      recs.push({ priority: "alta", text: "Dependencia funcional severa - Evaluar necesidad de apoyo domiciliario", icon: Activity });
    }
    if (mmse !== undefined && mmse < 24) {
      recs.push({ priority: "alta", text: "Deterioro cognitivo - Considerar evaluación neurológica", icon: Brain });
    }
    if (yesavage !== undefined && yesavage > 8) {
      recs.push({ priority: "media", text: "Depresión moderada-severa - Valorar tratamiento", icon: Smile });
    }
    if (morse !== undefined && morse > 45) {
      recs.push({ priority: "alta", text: "Alto riesgo de caídas - Implementar medidas preventivas", icon: AlertTriangle });
    }
    if (mna !== undefined && mna < 8) {
      recs.push({ priority: "alta", text: "Desnutrición - Referir a nutrición", icon: Apple });
    }

    // Check if consultation is old
    if (latestConsultation) {
      const daysSince = Math.floor((new Date() - new Date(latestConsultation.fecha_consulta || latestConsultation.created_at)) / (1000 * 60 * 60 * 24));
      if (daysSince > 90) {
        recs.push({ priority: "media", text: `Última consulta hace ${daysSince} días - Programar control`, icon: Calendar });
      }
    } else {
      recs.push({ priority: "alta", text: "Sin consultas previas - Realizar evaluación inicial completa", icon: ClipboardPlus });
    }

    return recs.sort((a, b) => a.priority === "alta" ? -1 : 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Paciente no encontrado</p>
        <Link to="/pacientes">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a pacientes
          </Button>
        </Link>
      </div>
    );
  }

  const isArchived = String(patient?.status || "").toLowerCase() === "archived";

  const recommendations = getFollowUpRecommendations();

  // ── Longitudinal history helpers ────────────────────────────────────────
  const startEditHistory = () => {
    setHistEdit({
      antecedentes_patologicos: patient.ultimo_antecedente_patologico || (patient.enfermedades_cronicas || []).join(", ") || "",
      antecedentes_quirurgicos: patient.latest_antecedentes?.quirurgicos || "",
      antecedentes_no_patologicos: patient.latest_antecedentes?.no_patologicos || "",
      antecedentes_familiares: patient.latest_antecedentes?.familiares || "",
      alergias: (patient.alergias || []).join(", "),
      medicamentos_actuales: (patient.medicamentos_actuales || []).join(", "),
      latest_historia:
        (patient.latest_historia && String(patient.latest_historia).trim()) ||
        (patient.historia_enfermedad_actual && String(patient.historia_enfermedad_actual).trim()) ||
        "",
      latest_valoracion_social: patient.latest_valoracion_social || ""
    });
  };

  const handleSaveHistory = async () => {
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para editar historiales."); return; }
    if (!histEdit) return;
    setSavingHist(true);
    try {
      // Build minimal update payload — we use a partial-safe update via the patient PUT endpoint
      const alergiasList = histEdit.alergias
        ? histEdit.alergias.split(",").map(s => s.trim()).filter(Boolean)
        : patient.alergias || [];
      const medsList = histEdit.medicamentos_actuales
        ? histEdit.medicamentos_actuales.split(",").map(s => s.trim()).filter(Boolean)
        : patient.medicamentos_actuales || [];

      const updatePayload = {
        // Required fields (keep unchanged)
        nombres: patient.nombres,
        apellidos: patient.apellidos,
        fecha_nacimiento: patient.fecha_nacimiento,
        telefono: patient.telefono || "",
        direccion: patient.direccion || "",
        diagnostico_principal: patient.diagnostico_principal || "",
        cuidador_principal: patient.cuidador_principal || "",
        telefono_cuidador: patient.telefono_cuidador || "",
        enfermedades_cronicas: patient.enfermedades_cronicas || [],
        chronic_conditions: patient.chronic_conditions || [],
        // Updated fields
        alergias: alergiasList,
        medicamentos_actuales: medsList,
        ultimo_antecedente_patologico: histEdit.antecedentes_patologicos,
        latest_antecedentes: {
          patologicos: histEdit.antecedentes_patologicos,
          quirurgicos: histEdit.antecedentes_quirurgicos,
          no_patologicos: histEdit.antecedentes_no_patologicos,
          familiares: histEdit.antecedentes_familiares,
          alergias: histEdit.alergias
        },
        latest_historia: histEdit.latest_historia,
        latest_valoracion_social: histEdit.latest_valoracion_social
      };

      await updatePatient(id, updatePayload);
      setPatient(prev => ({ ...prev, ...updatePayload }));
      setHistEdit(null);
      toast.success("Historia clínica actualizada");
    } catch {
      toast.error("Error al guardar la historia clínica");
    } finally {
      setSavingHist(false);
    }
  };

  // ── Longitudinal history tab component ─────────────────────────────────
  const renderHistoryTab = () => {
    const ant = patient.latest_antecedentes || {};
    const isEditing = histEdit !== null;
    const val = (field) => isEditing ? histEdit[field] : undefined;
    const set = (field) => (e) => setHistEdit(prev => ({ ...prev, [field]: e.target.value }));

    const persistedHistoria = getPersistedPatientHistoria(patient);
    const fallbackHistoria = persistedHistoria
      ? ""
      : buildLongitudinalHistoryFromConsultations(consultations);
    const historiaActualDisplay = persistedHistoria || fallbackHistoria;
    const historiaEmptyMessage = "No se documenta información clínica relevante.";

    const ReadField = ({ label, value, icon: Icon, emptyText = "Sin registrar" }) => (
      <div className="space-y-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
          {Icon && <Icon className="h-3.5 w-3.5" />}{label}
        </p>
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
          {value ? value : <span className="text-slate-400 italic">{emptyText}</span>}
        </p>
      </div>
    );

    const EditField = ({ label, field, icon: Icon, multiline = true }) => (
      <div className="space-y-1">
        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
          {Icon && <Icon className="h-3.5 w-3.5" />}{label}
        </Label>
        {multiline ? (
          <Textarea
            value={val(field)}
            onChange={set(field)}
            className="min-h-[72px] resize-none text-sm"
            placeholder={`Registrar ${label.toLowerCase()}...`}
          />
        ) : (
          <Input value={val(field)} onChange={set(field)} className="text-sm" placeholder={`Registrar ${label.toLowerCase()}...`} />
        )}
      </div>
    );

    return (
      <div className="space-y-5">
        {/* Actions bar */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-800">Historia Clínica Longitudinal</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Antecedentes persistentes del paciente. Se actualiza automáticamente desde cada consulta.
            </p>
          </div>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={startEditHistory} disabled={isDemoUser}>
              <BookOpen className="h-4 w-4 mr-2" />
              Editar historia
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHistEdit(null)} disabled={savingHist}>
                <X className="h-4 w-4 mr-1" />Cancelar
              </Button>
              <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={handleSaveHistory} disabled={savingHist || isDemoUser}>
                {savingHist ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Guardar
              </Button>
            </div>
          )}
        </div>

        {/* Historia actual: perfil persistido (prioridad) o resumen automático desde consultas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-sky-600" />
              Historia de la Enfermedad Actual
              {(persistedHistoria || fallbackHistoria) && (
                <Badge variant="outline" className="ml-auto text-[10px]">
                  {persistedHistoria ? "Perfil del paciente" : "Resumen desde consultas"}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <EditField field="latest_historia" label="Historia de la enfermedad actual" multiline />
            ) : (
              <ReadField
                label=""
                value={historiaActualDisplay}
                emptyText={historiaEmptyMessage}
              />
            )}
          </CardContent>
        </Card>

        {/* Antecedentes grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Pathological */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-sky-600" />
                Antecedentes Patológicos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing
                ? <EditField field="antecedentes_patologicos" label="Antecedentes patológicos" />
                : <ReadField label="" value={patient.ultimo_antecedente_patologico || ant.patologicos || (patient.enfermedades_cronicas || []).join(", ")} />
              }
            </CardContent>
          </Card>

          {/* Surgical */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-sky-600" />
                Antecedentes Quirúrgicos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing
                ? <EditField field="antecedentes_quirurgicos" label="Antecedentes quirúrgicos" />
                : <ReadField label="" value={ant.quirurgicos} />
              }
            </CardContent>
          </Card>

          {/* Non-pathological */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Heart className="h-4 w-4 text-sky-600" />
                Antecedentes No Patológicos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing
                ? <EditField field="antecedentes_no_patologicos" label="Antecedentes no patológicos" />
                : <ReadField label="" value={ant.no_patologicos} />
              }
            </CardContent>
          </Card>

          {/* Family */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-sky-600" />
                Antecedentes Familiares
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing
                ? <EditField field="antecedentes_familiares" label="Antecedentes familiares" />
                : <ReadField label="" value={ant.familiares} />
              }
            </CardContent>
          </Card>
        </div>

        {/* Allergies + current medications */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Alergias
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-1">
                  <EditField field="alergias" label="Alergias (separar con coma)" multiline={false} />
                </div>
              ) : (
                (patient.alergias || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(patient.alergias || []).map((a, i) => (
                      <Badge key={i} variant="outline" className="bg-amber-50 border-amber-200 text-amber-700">{a}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Sin alergias registradas</p>
                )
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Pill className="h-4 w-4 text-sky-600" />
                Medicamentos Actuales
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <EditField field="medicamentos_actuales" label="Medicamentos (separar con coma)" multiline />
              ) : (
                (patient.medicamentos_actuales || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(patient.medicamentos_actuales || []).map((m, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{m}</Badge>
                    ))}
                    {(patient.medicamentos_actuales || []).length >= 5 && (
                      <p className="w-full text-xs text-amber-600 flex items-center gap-1 mt-1">
                        <AlertTriangle className="h-3 w-3" />
                        Polifarmacia ({patient.medicamentos_actuales.length} medicamentos)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Sin medicamentos registrados</p>
                )
              )}
            </CardContent>
          </Card>
        </div>

        {/* Social assessment */}
        {(patient.latest_valoracion_social || isEditing) && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-violet-600" />
                Valoración Social
                <Badge variant="outline" className="ml-auto text-[10px]">Última consulta</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing
                ? <EditField field="latest_valoracion_social" label="Valoración social" />
                : <ReadField label="" value={patient.latest_valoracion_social} />
              }
            </CardContent>
          </Card>
        )}

        {/* Consultation narrative timeline */}
        {consultations.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4 text-sky-600" />
                Narrativa por Consultas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {consultations.slice(0, 10).map((c, i) => {
                  const narrativeParts = [
                    c.historia_enfermedad_actual,
                    c.impresion_clinica,
                    c.sindrome_geriatrico
                  ].filter(Boolean);
                  const dateStr = c.fecha_consulta || c.created_at;
                  const hasContent = narrativeParts.length > 0 || c.motivo_consulta;
                  return (
                    <div key={c.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`h-2.5 w-2.5 rounded-full mt-1 flex-shrink-0 ${i === 0 ? "bg-sky-500" : "bg-slate-300"}`} />
                        {i < consultations.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                      </div>
                      <div className="pb-4 flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-500">
                          {dateStr ? new Date(dateStr).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                          {" · "}{c.tipo_consulta || "Consulta"}
                        </p>
                        {!hasContent && <p className="text-sm text-slate-400 italic mt-0.5">Consulta registrada</p>}
                        {c.motivo_consulta && (
                          <p className="text-sm font-medium text-slate-800 mt-0.5">{c.motivo_consulta}</p>
                        )}
                        {c.historia_enfermedad_actual && (
                          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.historia_enfermedad_actual}</p>
                        )}
                        {c.impresion_clinica && (
                          <p className="text-xs text-slate-500 mt-1 italic">{c.impresion_clinica}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patient-dashboard">
      {isDemoUser && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          ✨ Modo demo — Estás viendo el perfil completo de un paciente de ejemplo. Para gestionar tus propios pacientes <a href="/register" className="font-semibold underline">crea tu cuenta gratis</a>.
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/pacientes">
            <Button variant="ghost" size="icon" data-testid="back-to-patients">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-800">
                {patient.nombres} {patient.apellidos}
              </h1>
              <RiskBadge level={patient.nivel_riesgo || "bajo"} />
              {isArchived && (
                <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50">
                  Archivado
                </Badge>
              )}
            </div>
            <p className="text-slate-500 mt-0.5">
              {patient.edad} años • {patient.genero || "N/A"} • {patient.diagnostico_principal || "Sin diagnóstico principal"}
            </p>
            {detectedSyndromes.length > 0 && (
              <p className="text-amber-700 text-sm mt-1">
                Síndromes: {detectedSyndromes.map(s => s.name).join(", ")}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isArchived ? (
            <Button className="bg-sky-600 hover:bg-sky-700" size="sm" data-testid="new-consultation-btn" disabled>
              <ClipboardPlus className="h-4 w-4 mr-2" />
              {consultations.length === 0 ? "Nueva Consulta Inicial" : "Reconsulta / Seguimiento"}
            </Button>
          ) : (
            <Link to={`/nueva-consulta/${id}`}>
              <Button className="bg-sky-600 hover:bg-sky-700" size="sm" data-testid="new-consultation-btn">
                <ClipboardPlus className="h-4 w-4 mr-2" />
                {consultations.length === 0 ? "Nueva Consulta Inicial" : "Reconsulta / Seguimiento"}
              </Button>
            </Link>
          )}
          <Link to={`/pacientes/${id}/dashboard-v1`}>
            <Button variant="outline" size="sm" data-testid="try-new-dashboard-btn">
              Probar nuevo expediente
              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Beta</span>
            </Button>
          </Link>
        </div>
      </div>

      {isArchived && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="archived-patient-banner">
          Paciente archivado. El expediente y sus consultas se conservan. Restaure al paciente desde la lista de archivados para iniciar o continuar atención.
        </div>
      )}

      <ExternalAssessmentsSection patientId={id} archived={isArchived} />

      {!profileComplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <AlertTriangle className="h-4 w-4 inline mr-2 text-amber-600" />
          <span className="text-amber-800">
            Complete su perfil en <Link to="/configuracion" className="underline font-medium">Configuración</Link> para generar documentos clínicos.
          </span>
        </div>
      )}

      {/* ── Tab Navigation ───────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-slate-200 pb-0">
        {[
          { key: "perfil",  label: "Perfil Clínico",        icon: Activity },
          { key: "historia",label: "Historia Longitudinal",  icon: BookOpen  }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors
              ${activeTab === key
                ? "bg-white border-slate-200 text-sky-700 -mb-px"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab: Historia Longitudinal ───────────────────────────────────── */}
      {activeTab === "historia" && renderHistoryTab()}

      {/* ── Tab: Perfil Clínico (existing content) ──────────────────────── */}
      {activeTab === "perfil" && <>

      {/* Clinical Status Panel - at-a-glance overview */}
      <ClinicalStatusPanel
        displayScales={displayScales}
        detectedSyndromes={detectedSyndromes}
        latestConsultation={latestConsultation}
      />

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Patient Summary */}
        <div className="space-y-4">
          {/* Patient Info Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-sky-600" />
                Resumen del Paciente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Edad</p>
                  <p className="font-medium">{patient.edad} años</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Sexo</p>
                  <p className="font-medium">{patient.genero || "N/A"}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Teléfono</p>
                  <p className="font-medium">{patient.telefono || "N/A"}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Nacimiento</p>
                  <p className="font-medium">{patient.fecha_nacimiento || "N/A"}</p>
                </div>
              </div>

              <Separator />

              {/* Caregiver */}
              <div>
                <p className="text-slate-500 text-xs flex items-center gap-1 mb-1">
                  <Users className="h-3 w-3" /> Cuidador / Contacto
                </p>
                <p className="font-medium text-sm">
                  {patient.cuidador_principal || "No especificado"}
                </p>
                {patient.telefono_cuidador && (
                  <p className="text-xs text-slate-600">{patient.telefono_cuidador}</p>
                )}
              </div>

              <Separator />

              {/* Chronic Conditions */}
              <div>
                <p className="text-slate-500 text-xs mb-2">Condiciones Crónicas</p>
                <div className="flex flex-wrap gap-1">
                  {patient.chronic_conditions?.length > 0 ? (
                    patient.chronic_conditions.map((cond, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-slate-50">
                        {cond}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400">Ninguna registrada</span>
                  )}
                </div>
              </div>

              <Separator />

              {/* Current Medications */}
              <div>
                <p className="text-slate-500 text-xs flex items-center gap-1 mb-2">
                  <Pill className="h-3 w-3" /> Medicamentos Actuales
                </p>
                <div className="flex flex-wrap gap-1">
                  {patient.medicamentos_actuales?.length > 0 ? (
                    patient.medicamentos_actuales.slice(0, 5).map((med, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {med}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400">Ninguno registrado</span>
                  )}
                  {patient.medicamentos_actuales?.length > 5 && (
                    <Badge variant="outline" className="text-xs">
                      +{patient.medicamentos_actuales.length - 5} más
                    </Badge>
                  )}
                </div>
                {patient.medicamentos_actuales?.length >= 5 && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Polifarmacia ({patient.medicamentos_actuales.length} medicamentos)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Active Diagnoses */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-sky-600" />
                Diagnósticos Activos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {patient.diagnostico_principal ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Badge className="bg-sky-100 text-sky-700 text-xs">Principal</Badge>
                    <p className="text-sm font-medium">{patient.diagnostico_principal}</p>
                  </div>
                  {latestConsultation?.diagnostico && (
                    <div className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded">
                      {latestConsultation.diagnostico}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">Sin diagnósticos registrados</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Center Column - Last Consultation & Scales */}
        <div className="space-y-4">
          {/* Last Consultation */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-sky-600" />
                Última Consulta
              </CardTitle>
            </CardHeader>
            <CardContent>
              {latestConsultation ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">
                        {(latestConsultation.fecha_consulta || latestConsultation.created_at)
                          ? new Date(latestConsultation.fecha_consulta || latestConsultation.created_at).toLocaleDateString("es-ES", {
                              day: "numeric", month: "long", year: "numeric"
                            })
                          : "—"}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {latestConsultation.tipo_consulta || "Control"}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Motivo de consulta</p>
                    <p className="text-sm">{latestConsultation.motivo_consulta || "No especificado"}</p>
                  </div>

                  {latestConsultation.impresion_clinica && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Impresión clínica</p>
                      <p className="text-sm text-slate-700 line-clamp-3">
                        {latestConsultation.impresion_clinica}
                      </p>
                    </div>
                  )}

                  <Link 
                    to={`/consulta/${latestConsultation.id}`}
                    className="inline-flex items-center text-xs text-sky-600 hover:underline mt-2"
                  >
                    Ver consulta completa <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-400 mb-2">Sin consultas previas</p>
                  {isArchived ? (
                    <Button size="sm" variant="outline" disabled>
                      <ClipboardPlus className="h-4 w-4 mr-2" />
                      Crear primera consulta
                    </Button>
                  ) : (
                    <Link to={`/nueva-consulta/${id}`}>
                      <Button size="sm" variant="outline">
                        <ClipboardPlus className="h-4 w-4 mr-2" />
                        Crear primera consulta
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Escalas Completadas */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-sky-600" />
                Escalas Evaluadas
              </CardTitle>
              <p className="text-xs text-slate-500">Tomadas de la última consulta registrada</p>
            </CardHeader>
            <CardContent>
              {(() => {
                const allScaleIds = ['barthel', 'lawton', 'katz', 'mmse', 'pfeiffer', 'yesavage', 'mna_sf', 'morse', 'tinetti', 'frail', 'cfs', 'charlson', 'gijon', 'eva_dolor', 'norton', 'gad7', 'sarc_f', 'tug', 'cam', 'mini_cog', 'moca', 'polifarmacia_simple', 'phq9', 'sppb', 'zarit'];
                const scaleMax = { barthel: 100, lawton: 8, katz: 6, mmse: 30, pfeiffer: 10, yesavage: 15, mna_sf: 14, morse: 125, tinetti: 28, frail: 5, cfs: 9, charlson: 37, gijon: 20, eva_dolor: 10, norton: 20, gad7: 21, sarc_f: 10, tug: null, cam: null, mini_cog: null, moca: 30, polifarmacia_simple: null, phq9: 27, sppb: 12, zarit: 48 };
                // Interpretation functions for quick label when not stored
                const getInterp = (key, score) => {
                  const interps = {
                    barthel: s => s >= 90 ? "Independiente" : s >= 60 ? "Dep. leve" : s >= 40 ? "Dep. moderada" : "Dep. severa",
                    katz: s => s === 6 ? "Independiente" : s >= 4 ? "Dep. leve" : "Dep. severa",
                    lawton: s => s === 8 ? "Independiente" : s >= 5 ? "Dep. leve" : "Dep. severa",
                    mmse: s => s >= 24 ? "Normal" : s >= 18 ? "Deterioro leve" : s >= 10 ? "Moderado" : "Severo",
                    pfeiffer: s => s <= 2 ? "Normal" : s <= 4 ? "Deterioro leve" : s <= 7 ? "Moderado" : "Severo",
                    yesavage: (s) => interpretYesavageLabel(s),
                    mna_sf: s => s >= 12 ? "Normal" : s >= 8 ? "Riesgo nutricional" : "Malnutrición",
                    morse: s => s <= 24 ? "Riesgo bajo" : s <= 44 ? "Riesgo moderado" : "Riesgo alto",
                    tinetti: s => s >= 24 ? "Riesgo bajo" : s >= 19 ? "Riesgo moderado" : "Riesgo alto",
                    frail: s => s === 0 ? "Robusto" : s <= 2 ? "Pre-frágil" : "Frágil",
                    norton: s => s >= 14 ? "Riesgo mínimo" : s >= 12 ? "Riesgo evidente" : "Riesgo alto UPP",
                    gad7: s => s <= 4 ? "Ansiedad mínima" : s <= 9 ? "Ansiedad leve" : s <= 14 ? "Ansiedad moderada" : "Ansiedad grave",
                    sarc_f: s => s <= 3 ? "Bajo riesgo (tamizaje)" : s <= 6 ? "Riesgo intermedio" : "Alto riesgo",
                    tug: s => {
                      const n = Number(s);
                      if (Number.isNaN(n)) return "";
                      if (n < 10) return "Dentro de rango";
                      if (n < 20) return "Conservada";
                      if (n < 30) return "Riesgo aumentado (≥20 s)";
                      return "Alteración marcada";
                    },
                    cam: (s) =>
                      s === true || s === 1 || s === "1"
                        ? "Compatible con delirium"
                        : "No cumple criterios de delirium",
                    mini_cog: (s) =>
                      s === true || s === 1 || s === "1"
                        ? "Tamizaje positivo (Mini-Cog)"
                        : "Tamizaje negativo (Mini-Cog)",
                    moca: (s) => {
                      const n = Number(s);
                      if (Number.isNaN(n)) return "";
                      if (n >= 26) return "Cognición en rango esperado";
                      if (n >= 18) return "Sospecha de deterioro leve";
                      if (n >= 10) return "Deterioro moderado";
                      return "Deterioro severo";
                    },
                    polifarmacia_simple: (s) => {
                      const n = Number(s);
                      if (Number.isNaN(n)) return "";
                      if (n <= 4) return "Bajo riesgo";
                      if (n <= 9) return "Polifarmacia";
                      return "Polifarmacia severa";
                    },
                  };
                  return interps[key] ? interps[key](score) : "";
                };
                const getRiskClass = (key, score) => {
                  const highRisk = {
                    barthel: s => s < 60, katz: s => s < 4, lawton: s => s < 4,
                    mmse: s => s < 24, pfeiffer: s => s > 4, yesavage: s => s >= 5,
                    mna_sf: s => s < 12, morse: s => s >= 45, tinetti: s => s < 19,
                    frail: s => s >= 3, norton: s => s < 12,
                    gad7: s => s >= 10, sarc_f: s => s >= 7, tug: s => Number(s) >= 30,
                    cam: (s) => s === true || s === 1 || s === "1",
                    mini_cog: (s) => s === true || s === 1 || s === "1",
                    moca: (s) => {
                      const n = Number(s);
                      return !Number.isNaN(n) && n < 26 && n >= 18;
                    },
                    polifarmacia_simple: (s) => Number(s) >= 10,
                  };
                  const medRisk = {
                    barthel: s => s >= 60 && s < 90, katz: s => s === 4 || s === 5,
                    mmse: s => s >= 18 && s < 24, mna_sf: s => s >= 8 && s < 12,
                    morse: s => s >= 25 && s < 45, tinetti: s => s >= 19 && s < 24, frail: s => s <= 2 && s > 0,
                    gad7: s => s >= 5 && s < 10, sarc_f: s => s >= 4 && s <= 6, tug: s => {
                      const n = Number(s);
                      return !Number.isNaN(n) && n >= 20 && n < 30;
                    },
                    moca: (s) => {
                      const n = Number(s);
                      return !Number.isNaN(n) && n < 18 && n >= 10;
                    },
                    polifarmacia_simple: (s) => {
                      const n = Number(s);
                      return !Number.isNaN(n) && n >= 5 && n <= 9;
                    },
                  };
                  if (highRisk[key]?.(score)) return "bg-red-50 border-red-200 text-red-800";
                  if (medRisk[key]?.(score)) return "bg-amber-50 border-amber-200 text-amber-800";
                  return "bg-emerald-50 border-emerald-200 text-emerald-800";
                };
                const sourceScales = Object.keys(latestConsultationScales).length > 0 ? latestConsultationScales : {};
                const completed = allScaleIds.filter(k => {
                  const v = sourceScales[k]?.puntaje ?? sourceScales[k]?.value;
                  return v !== undefined && v !== null;
                });
                const showIds = completed.length > 0 ? completed : [];
                if (showIds.length === 0) {
                  return (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500 mb-2">Sin escalas registradas en la última consulta.</p>
                      {isArchived ? (
                        <Button size="sm" variant="outline" disabled>
                          <Scale className="h-4 w-4 mr-2" />
                          Nueva consulta
                        </Button>
                      ) : (
                        <Link to={`/nueva-consulta/${id}`}>
                          <Button size="sm" variant="outline">
                            <Scale className="h-4 w-4 mr-2" />
                            Nueva consulta
                          </Button>
                        </Link>
                      )}
                    </div>
                  );
                }
                return (
                  <>
                    <div className="space-y-2">
                      {showIds.map(scaleKey => {
                        const scale = sourceScales[scaleKey];
                        const puntaje = scale?.puntaje ?? scale?.value;
                        const interp = scale?.interpretacion || getInterp(scaleKey, puntaje);
                        const max = scaleMax[scaleKey];
                        const riskClass = getRiskClass(scaleKey, puntaje);
                        return (
                          <div
                            key={scaleKey}
                            className={`flex items-center justify-between p-2.5 rounded-lg border ${riskClass}`}
                          >
                            <div>
                              <p className="text-xs font-medium">{SCALE_LABELS[scaleKey] || scaleKey}</p>
                              {interp && <p className="text-[10px] opacity-75">{interp}</p>}
                            </div>
                            <div className="text-right">
                              <span className="text-lg font-bold">
                                {scaleKey === "cam" || scaleKey === "mini_cog"
                                  ? puntaje === true || puntaje === 1 || puntaje === "1"
                                    ? "Positivo"
                                    : puntaje === false || puntaje === 0 || puntaje === "0"
                                      ? "Negativo"
                                      : String(puntaje ?? "")
                                  : puntaje}
                              </span>
                              {scaleKey === "tug" ? (
                                <span className="text-xs opacity-60"> s</span>
                              ) : scaleKey === "polifarmacia_simple" ? (
                                <span className="text-xs opacity-60"> med.</span>
                              ) : scaleKey === "cam" || scaleKey === "mini_cog" ? null : (
                                <span className="text-xs opacity-60">/{max ?? 100}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {isArchived ? (
                      <span className="inline-flex items-center text-xs text-slate-400 mt-3">
                        Nueva consulta
                      </span>
                    ) : (
                    <Link to={`/nueva-consulta/${id}`} className="inline-flex items-center text-xs text-sky-600 hover:underline mt-3">
                      Nueva consulta <ChevronRight className="h-3 w-3" />
                    </Link>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Follow-ups & Charts */}
        <div className="space-y-4">
          {/* Follow-up Recommendations */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-sky-600" />
                Recomendaciones de Seguimiento
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recommendations.length > 0 ? (
                <div className="space-y-2">
                  {recommendations.slice(0, 4).map((rec, idx) => {
                    const Icon = rec.icon;
                    return (
                      <div 
                        key={idx}
                        className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                          rec.priority === "alta" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"
                        }`}
                      >
                        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{rec.text}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 p-3 rounded-lg">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm">Sin alertas pendientes</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions — modelo 1 consulta = 1 documento */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardPlus className="h-4 w-4 text-sky-600" />
                Acciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => { if (!isArchived) navigate(`/nueva-consulta/${id}`); }}
                disabled={isArchived}
                data-testid="new-consultation-btn"
              >
                <ClipboardPlus className="h-4 w-4 mr-2" />
                {consultations.length === 0 ? "Nueva consulta" : "Nueva consulta"}
              </Button>
              {hasDraft && !isArchived && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  onClick={() => navigate(`/nueva-consulta/${id}`)}
                  data-testid="continue-draft-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Continuar borrador
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => { setActiveTab("historia"); }}
                data-testid="view-history-btn"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Ver historia clínica
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Geriatric Profile Visual - uses merged displayScales */}
      <GeriatricProfileVisual scales={displayScales} />

      {/* Detected Geriatric Syndromes */}
      {detectedSyndromes.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Síndromes Geriátricos Detectados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {detectedSyndromes.map((s, i) => (
                <Badge key={i} variant="outline" className="bg-amber-50 border-amber-300 text-amber-800">
                  {s.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evolution Charts - dynamic: only scales with data for this patient */}
      <Card id="seccion-evolucion" className="border-slate-200 scroll-mt-24">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-sky-600" />
            Evolución de Escalas
          </CardTitle>
        </CardHeader>
        <CardContent>
            {!ENABLE_SCALES_HISTORY ? (
              <p className="text-sm text-slate-400 py-4 text-center">
                Evolución de escalas disponible próximamente.
              </p>
            ) : (
              (() => {
                const SCALE_CHART_CONFIG = {
                  barthel: { label: "Barthel (Funcional)", color: "#059669", max: 100 },
                  lawton: { label: "Lawton (IADL)", color: "#0d9488", max: 8 },
                  katz: { label: "Katz (AVD)", color: "#0f766e", max: 6 },
                  mmse: { label: "MMSE (Cognitivo)", color: "#0284c7", max: 30 },
                  pfeiffer: { label: "Pfeiffer (Cognitivo)", color: "#0369a1", max: 10 },
                  yesavage: { label: "Yesavage (Ánimo)", color: "#7c3aed", max: 15 },
                  mna_sf: { label: "MNA (Nutricional)", color: "#d97706", max: 14 },
                  morse: { label: "Morse (Caídas)", color: "#dc2626", max: 125 },
                  tinetti: { label: "Tinetti (Marcha)", color: "#b91c1c", max: 28 },
                  frail: { label: "FRAIL (Fragilidad)", color: "#ea580c", max: 5 },
                  gijon: { label: "Gijón (Social)", color: "#9333ea", max: 20 },
                  cfs: { label: "CFS (Fragilidad)", color: "#c2410c", max: 9 },
                  charlson: { label: "Charlson (Comorbilidad)", color: "#4f46e5", max: 37 },
                  norton: { label: "Norton (UPP)", color: "#6d28d9", max: 20 }
                };
                const scalesWithData = Object.keys(scaleHistory || {}).filter(k => {
                  const arr = scaleHistory[k];
                  return Array.isArray(arr) && arr.length > 0;
                });
                if (scalesWithData.length === 0) {
                  return (
                    <p className="text-sm text-slate-400 py-4 text-center">
                      No hay datos de evolución de escalas. Las escalas se evaluarán en las consultas.
                    </p>
                  );
                }
                return (
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {scalesWithData.map(scaleId => {
                      const cfg = SCALE_CHART_CONFIG[scaleId] || { label: scaleId, color: "#64748b", max: 100 };
                      return (
                        <div key={scaleId}>
                          <p className="text-sm font-medium text-slate-700 mb-2">{cfg.label}</p>
                          <EvolutionChart data={scaleHistory[scaleId]} title={cfg.label} dataKey="value" color={cfg.color} maxValue={cfg.max} />
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
        </CardContent>
      </Card>

      {/* Consultation History - always shown if consultations exist */}
      {consultations.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-sky-600" />
              Historial de Consultas
              <Badge variant="outline" className="ml-auto text-xs">{consultations.length} consulta{consultations.length !== 1 ? "s" : ""}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {consultations.slice(0, 8).map((consult, idx) => (
                <div 
                  key={consult.id}
                  className={`rounded-lg border transition-colors ${idx === 0 ? "border-sky-200 bg-sky-50/50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
                >
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center ${idx === 0 ? "bg-sky-100" : "bg-slate-200"}`}>
                        <FileText className={`h-4 w-4 ${idx === 0 ? "text-sky-600" : "text-slate-500"}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-800">{consult.tipo_consulta || "Consulta"}</p>
                          {idx === 0 && <Badge className="bg-sky-100 text-sky-700 border-sky-200 text-[10px] px-1.5 py-0">Última</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">
                          {(consult.fecha_consulta || consult.created_at)
                            ? new Date(consult.fecha_consulta || consult.created_at).toLocaleDateString("es-ES", {
                                day: "numeric", month: "long", year: "numeric"
                              })
                            : "—"}
                          {consult.motivo_consulta ? ` · ${consult.motivo_consulta.substring(0, 45)}${consult.motivo_consulta.length > 45 ? "..." : ""}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Link to={`/consulta/${consult.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
                          data-testid="view-consultation-detail-btn"
                          title="Ver detalle del documento clínico"
                        >
                          Ver detalle
                        </Button>
                      </Link>
                      <Link to={`/entregable/${consult.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-xs text-sky-700 border-sky-200 hover:bg-sky-50"
                          data-testid="generar-entregable-from-patient-btn"
                          title="Documentos e impresión desde un solo flujo"
                        >
                          <FileStack className="h-3.5 w-3.5 mr-1" />
                          Generar documento
                        </Button>
                      </Link>
                    </div>
                  </div>
                  {/* Syndromes detected in this consultation */}
                  {idx === 0 && detectedSyndromes.length > 0 && (
                    <div className="px-3 pb-2.5 flex flex-wrap gap-1">
                      {detectedSyndromes.slice(0, 4).map((s, si) => (
                        <span key={si} className="text-[10px] bg-white border border-sky-200 text-sky-700 rounded px-1.5 py-0.5">{s.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {consultations.length > 8 && (
              <p className="text-xs text-slate-400 mt-2 text-center">+{consultations.length - 8} consultas anteriores</p>
            )}
          </CardContent>
        </Card>
      )}

      </>}
    </div>
  );
}
